import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  sheetScaleFromCalibration,
  type PixelPoint,
  type SheetScale,
} from '@otto/onscreen-engine';
import type { SubmissionPage, TakeoffSessionRow } from '../../lib/types.ts';
import { COLORS, mono } from './styles.ts';
import {
  PDF_NATIVE_DPI,
  parseFeetInput,
  newId,
  type CalibStatus,
  type CivilJobData,
  type TakeoffItem,
  type Tool,
} from './helpers.ts';
import { ToolBar } from './ToolBar.tsx';
import { DistanceDialog } from './DistanceDialog.tsx';
import { PagePicker } from './PagePicker.tsx';
import { ScaleReadout } from './ScaleReadout.tsx';
import { ItemsCard } from './ItemsCard.tsx';
import { GeometryOverlay } from './GeometryOverlay.tsx';
import { Card, MetaRow } from './primitives.tsx';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

interface Props {
  sourceUrl: string;
  pages: readonly SubmissionPage[];
  jobData: CivilJobData;
  sessionRow: TakeoffSessionRow | null;
  onScaleSet: (scale: SheetScale, pageIndex: number) => void;
  onPageChange: (pageIndex: number) => void;
  onItemsChange: (pageIndex: number, updater: (prev: readonly TakeoffItem[]) => TakeoffItem[]) => void;
}

const POLYGON_CLOSE_PX = 12;

export function CivilCanvas({
  sourceUrl,
  pages,
  jobData,
  sessionRow,
  onScaleSet,
  onPageChange,
  onItemsChange,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const cancelRenderRef = useRef<(() => void) | null>(null);

  const [pdfReady, setPdfReady] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [renderStatus, setRenderStatus] = useState<'idle' | 'rendering' | 'error'>('idle');
  const [renderScale, setRenderScale] = useState(1);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const pageIndex = jobData.pageIndex;
  const page = jobData.pages[pageIndex];
  const scale = page?.scale ?? null;
  const items = useMemo(() => page?.items ?? [], [page?.items]);

  const [tool, setTool] = useState<Tool>('pointer');
  const [calibStatus, setCalibStatus] = useState<CalibStatus>('idle');
  const [calibP1, setCalibP1] = useState<PixelPoint | null>(null);
  const [calibP2, setCalibP2] = useState<PixelPoint | null>(null);
  const [distanceInput, setDistanceInput] = useState('');
  const [calibError, setCalibError] = useState<string | null>(null);

  const [inProgress, setInProgress] = useState<PixelPoint[]>([]);
  const [cursor, setCursor] = useState<PixelPoint | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const polygonCloseHint = useMemo(() => {
    if (tool !== 'polygon' || inProgress.length < 3 || !cursor) return false;
    const first = inProgress[0];
    const dxCss = (cursor.x - first.x) * renderScale;
    const dyCss = (cursor.y - first.y) * renderScale;
    return Math.hypot(dxCss, dyCss) <= POLYGON_CLOSE_PX;
  }, [tool, inProgress, cursor, renderScale]);

  // ── Load PDF document once ─────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    async function loadDoc() {
      try {
        const doc = await pdfjsLib.getDocument({ url: sourceUrl }).promise;
        if (!active) {
          void doc.destroy();
          return;
        }
        pdfDocRef.current?.destroy();
        pdfDocRef.current = doc;
        setPageCount(doc.numPages);
        setPdfReady(true);
      } catch {
        // surfaces via render error
      }
    }
    void loadDoc();
    return () => {
      active = false;
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;
      setPdfReady(false);
    };
  }, [sourceUrl]);

  // ── Track container size ───────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Render current page ────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfReady || !canvasRef.current) return;
    if (containerSize.w === 0) return;

    cancelRenderRef.current?.();
    let cancelled = false;
    cancelRenderRef.current = () => {
      cancelled = true;
    };
    setRenderStatus('rendering');

    const pageNum = pageIndex + 1;

    async function render() {
      try {
        const doc = pdfDocRef.current;
        if (!doc || cancelled) return;
        const pdfPage = await doc.getPage(pageNum);
        if (cancelled) {
          pdfPage.cleanup();
          return;
        }

        const containerWidth = containerSize.w - 32;
        const baseVp = pdfPage.getViewport({ scale: 1 });
        const fitScale = Math.max(0.5, Math.min(containerWidth / baseVp.width, 3));
        const viewport = pdfPage.getViewport({ scale: fitScale });

        const canvas = canvasRef.current!;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined;

        const task = pdfPage.render({ canvas, viewport, transform });
        cancelRenderRef.current = () => {
          cancelled = true;
          task.cancel();
          pdfPage.cleanup();
        };
        await task.promise;
        if (!cancelled) {
          setRenderScale(fitScale);
          setCanvasSize({ w: Math.floor(viewport.width), h: Math.floor(viewport.height) });
          setRenderStatus('idle');
        }
      } catch {
        if (!cancelled) setRenderStatus('error');
      }
    }

    void render();
  }, [pdfReady, pageIndex, containerSize.w]);

  // Reset transient state when page or tool changes
  useEffect(() => {
    setCalibP1(null);
    setCalibP2(null);
    setCalibStatus('idle');
    setCalibError(null);
    setInProgress([]);
    setCursor(null);
    setSelectedItemId(null);
  }, [pageIndex]);

  useEffect(() => {
    setInProgress([]);
    setCursor(null);
    if (tool !== 'calibrate') {
      setCalibP1(null);
      setCalibP2(null);
      setCalibStatus('idle');
      setCalibError(null);
    }
  }, [tool]);

  // Auto-pick a sensible default tool when scale becomes available
  useEffect(() => {
    if (scale && tool === 'pointer') setTool('pointer');
  }, [scale, tool]);

  // ── Geometry mutations (updater style — race-safe under rapid clicks) ──────
  const updateItems = useCallback(
    (updater: (prev: readonly TakeoffItem[]) => TakeoffItem[]) => {
      onItemsChange(pageIndex, updater);
    },
    [onItemsChange, pageIndex],
  );

  const finishLinear = useCallback(() => {
    if (inProgress.length < 2) return;
    const next: TakeoffItem = { id: newId(), kind: 'linear', points: inProgress };
    updateItems((prev) => [...prev, next]);
    setInProgress([]);
    setCursor(null);
  }, [inProgress, updateItems]);

  const finishPolygon = useCallback(() => {
    if (inProgress.length < 3) return;
    const next: TakeoffItem = { id: newId(), kind: 'polygon', points: inProgress };
    updateItems((prev) => [...prev, next]);
    setInProgress([]);
    setCursor(null);
  }, [inProgress, updateItems]);

  const handleFinish = useCallback(() => {
    if (tool === 'linear') finishLinear();
    else if (tool === 'polygon') finishPolygon();
  }, [tool, finishLinear, finishPolygon]);

  const handleCancel = useCallback(() => {
    setInProgress([]);
    setCursor(null);
    setCalibP1(null);
    setCalibP2(null);
    setCalibStatus('idle');
    setCalibError(null);
  }, []);

  const handleDeleteItem = useCallback(
    (id: string) => {
      updateItems((prev) => prev.filter((it) => it.id !== id));
      setSelectedItemId((curr) => (curr === id ? null : curr));
    },
    [updateItems],
  );

  // ── Click/move handlers ────────────────────────────────────────────────────
  const cssToPdf = useCallback(
    (e: React.MouseEvent): PixelPoint => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) / renderScale,
        y: (e.clientY - rect.top) / renderScale,
      };
    },
    [renderScale],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const point = cssToPdf(e);

      if (tool === 'calibrate') {
        // First click → start; or recalibrate after a complete pair
        if (calibStatus === 'idle' || calibStatus === 'awaiting_first') {
          setCalibP1(point);
          setCalibP2(null);
          setCalibStatus('awaiting_second');
          setCalibError(null);
          return;
        }
        if (calibStatus === 'awaiting_second') {
          setCalibP2(point);
          setCalibStatus('awaiting_distance');
          setDistanceInput('');
          return;
        }
        return;
      }

      if (tool === 'count') {
        // Append to single existing count item, else create one. Updater
        // closes over `prev` so rapid clicks compose instead of clobbering.
        updateItems((prev) => {
          const existing = prev.find((i) => i.kind === 'count');
          if (existing) {
            const updated: TakeoffItem = { ...existing, points: [...existing.points, point] };
            return prev.map((i) => (i.id === existing.id ? updated : i));
          }
          return [...prev, { id: newId(), kind: 'count', points: [point] }];
        });
        return;
      }

      if (tool === 'linear') {
        setInProgress((curr) => [...curr, point]);
        return;
      }

      if (tool === 'polygon') {
        // Click near first point to close
        if (inProgress.length >= 3) {
          const first = inProgress[0];
          const dxCss = (point.x - first.x) * renderScale;
          const dyCss = (point.y - first.y) * renderScale;
          if (Math.hypot(dxCss, dyCss) <= POLYGON_CLOSE_PX) {
            finishPolygon();
            return;
          }
        }
        setInProgress((curr) => [...curr, point]);
        return;
      }
    },
    [tool, calibStatus, cssToPdf, updateItems, inProgress, renderScale, finishPolygon],
  );

  const handleDoubleClick = useCallback(() => {
    if (tool === 'linear' && inProgress.length >= 2) finishLinear();
  }, [tool, inProgress.length, finishLinear]);

  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (tool !== 'linear' && tool !== 'polygon') return;
      if (inProgress.length === 0) return;
      setCursor(cssToPdf(e));
    },
    [tool, inProgress.length, cssToPdf],
  );

  // Keyboard: Enter → finish, Esc → cancel
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleCancel();
        return;
      }
      if (e.key === 'Enter') {
        if (calibStatus === 'awaiting_distance') return; // dialog handles its own
        handleFinish();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleCancel, handleFinish, calibStatus]);

  const submitDistance = useCallback(() => {
    if (!calibP1 || !calibP2) {
      setCalibError('Click two points on the plan first.');
      setCalibStatus('idle');
      return;
    }
    const feet = parseFeetInput(distanceInput);
    if (feet == null || feet <= 0) {
      setCalibError('Enter a distance in feet (e.g. 50, 12.5).');
      return;
    }
    try {
      const calibrated = sheetScaleFromCalibration(calibP1, calibP2, feet);
      const result: SheetScale = { ...calibrated, dpi: PDF_NATIVE_DPI };
      onScaleSet(result, pageIndex);
      setCalibStatus('idle');
      setCalibP1(null);
      setCalibP2(null);
      setCalibError(null);
    } catch (err) {
      setCalibError(err instanceof Error ? err.message : 'Calibration failed');
    }
  }, [calibP1, calibP2, distanceInput, pageIndex, onScaleSet]);

  const handleToolChange = useCallback((next: Tool) => {
    setTool(next);
    if (next === 'calibrate') {
      setCalibStatus('awaiting_first');
    }
  }, []);

  const cursorStyle = tool === 'pointer' ? 'default' : 'crosshair';

  return (
    <>
      <ToolBar
        tool={tool}
        onToolChange={handleToolChange}
        calibStatus={calibStatus}
        scale={scale}
        inProgressCount={inProgress.length}
        onFinish={handleFinish}
        onCancel={handleCancel}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, alignItems: 'start' }}>
        <div
          ref={containerRef}
          style={{
            background: COLORS.bg,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            padding: 16,
            height: 'calc(100vh - 220px)',
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            position: 'relative',
          }}
        >
          {!pdfReady && <span style={mono(13, COLORS.textDim)}>Loading PDF…</span>}
          {pdfReady && renderStatus === 'error' && (
            <span style={mono(13, COLORS.red)}>Page render failed</span>
          )}
          <div
            ref={overlayRef}
            style={{ position: 'relative', display: pdfReady ? 'block' : 'none' }}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            onMouseMove={handleMove}
          >
            <canvas
              ref={canvasRef}
              style={{
                display: 'block',
                opacity: renderStatus === 'rendering' ? 0.3 : 1,
                transition: 'opacity 180ms ease',
                cursor: cursorStyle,
              }}
            />
            <GeometryOverlay
              width={canvasSize.w}
              height={canvasSize.h}
              renderScale={renderScale}
              scale={scale}
              items={items}
              selectedItemId={selectedItemId}
              hoveredItemId={hoveredItemId}
              inProgress={inProgress}
              cursor={cursor}
              tool={tool}
              calibP1={calibP1}
              calibP2={calibP2}
              onItemClick={(id) => setSelectedItemId((curr) => (curr === id ? null : id))}
              onItemHover={setHoveredItemId}
              showPolygonCloseHint={polygonCloseHint}
            />
          </div>
        </div>

        <div style={{ position: 'sticky', top: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card>
            <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 8 }}>
              PAGE
            </div>
            <PagePicker
              pageIndex={pageIndex}
              pageCount={pageCount}
              pages={pages}
              onChange={onPageChange}
            />
          </Card>

          <Card>
            <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 8 }}>
              SCALE
            </div>
            <ScaleReadout scale={scale} />
          </Card>

          <Card>
            <ItemsCard
              items={items}
              scale={scale}
              selectedItemId={selectedItemId}
              hoveredItemId={hoveredItemId}
              onSelect={setSelectedItemId}
              onHover={setHoveredItemId}
              onDelete={handleDeleteItem}
            />
          </Card>

          <Card>
            <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 8 }}>
              SESSION
            </div>
            <MetaRow label="DISCIPLINE" value="civil" />
            <MetaRow label="STATUS" value={sessionRow?.status ?? 'draft'} />
            <MetaRow label="PAGE" value={`${pageIndex + 1}${pageCount ? ` / ${pageCount}` : ''}`} />
            <MetaRow label="CALIBRATED" value={scale ? 'yes' : 'no'} />
            <MetaRow label="ITEMS" value={String(items.length)} />
          </Card>
        </div>
      </div>

      {calibStatus === 'awaiting_distance' && (
        <DistanceDialog
          value={distanceInput}
          onChange={setDistanceInput}
          onSubmit={submitDistance}
          onCancel={handleCancel}
          error={calibError}
        />
      )}
    </>
  );
}
